/* eslint-disable no-nested-ternary */
import { ChevronLeft, ChevronRight, InfoCircle } from '@saleshandy/icons';
import React, { memo, useEffect, useMemo, useState } from 'react';
import Lottie from 'lottie-react';
import Modal from '../../../../../../shared/design-system/ui/modal/modal';
import Select from '../../../../../../shared/design-system/components/select/select';
import AiVariant3StarIcon from '../../../../../../assets/json/lottieanimation/ai-variant-3-star-icon.json';
import { generateEmailPreviewMergeTag } from '../../utils/helper';
import {
  OverlayPopover,
  OverlayTooltip,
  Placement,
} from '../../../../../../shared/design-system/components/overlay';
import { RequestStatus } from '../../../../../../shared/enums/request-status';
import { accessibleOnClick } from '../../../../../../shared/utils/accessible-on-click';
import { executeOnRequestStatus } from '../../../../../../shared/utils';
import { AiColPromptPreviewModalProps, StoredPreview } from './types';
import { Cross } from '../../../../../../shared/svg';
import { br2nl } from '../../../../../sequence/shared/modals/email-modal/components/sidebar/tabs/utils/get-merge-tag-mapping-with-values';
import { convertHtmlToText } from '../../../../../sequence/shared/modals/email-modal/components/sidebar/tabs/utils/get-parsed-content-for-text-only-email';
import { stripChipTagsFromHtml } from '../../../../../../shared/editor/helpers';

const AiColPromptPreviewModal: React.FC<AiColPromptPreviewModalProps> = memo(
  ({
    show,
    onClose,
    enrichPromptData,
    getSingleProspectDetailsRequests,
    getAiColumnPreviewRequests,
    resetSingleProspectDetailsRequests,
    resetAiColumnTemplatePreviewRequest,
    resetAiColumnPreview,
    prospectList,
    getSingleProspectDetailsRequestStatus,
    aiColumnTemplatePreviewRequestStatus,
    aiColumnTemplatePreviewResponse,
    fields,
    selectors,
    setIsLoadingState,
    isLoadingState,
    selectedModalAiCreditUsagePerRow,
  }) => {
    // State's
    const [selectedPerson, setSelectedPerson] = useState('0');
    const [storedPreviews, setStoredPreviews] = useState<StoredPreview[]>([]);
    const [isCreditsPopoverOpen, setIsCreditsPopoverOpen] = useState(false);
    const handleClose = () => {
      setStoredPreviews([]);
      setIsLoadingState(false);
      resetAiColumnTemplatePreviewRequest();
      resetSingleProspectDetailsRequests();
      onClose();
    };

    // Function's
    const ProspectOptionList = useMemo(
      () =>
        prospectList?.map((item, index) => ({
          key: String(index),
          label: `${item?.firstName || ''} ${item?.lastName || ''}`,
          email: item?.email,
          id: item?.id,
        })) || [],
      [prospectList],
    );

    const selectedProspect = ProspectOptionList?.find(
      (p) => p?.key === selectedPerson,
    );

    const disbaleBtn =
      getSingleProspectDetailsRequestStatus === RequestStatus.Pending ||
      aiColumnTemplatePreviewRequestStatus === RequestStatus.Pending;
    // Check if prospect has stored preview
    const hasStoredPreview = (prospectKey: string) =>
      storedPreviews?.some((preview) => preview.prospectKey === prospectKey);

    // Get stored preview for current prospect
    const getStoredPreview = (prospectKey: string) =>
      storedPreviews?.find((preview) => preview.prospectKey === prospectKey);

    // Check if we should disable selection for new prospects
    const shouldDisableNewSelection = storedPreviews?.length >= 5;

    // Check if current prospect is disabled (no stored preview and limit reached)
    const isProspectDisabled = (prospectKey: string) =>
      shouldDisableNewSelection && !hasStoredPreview(prospectKey);

    // eslint-disable-next-line consistent-return
    const emailPreviewPrompt = useMemo(() => {
      const convertHTMLToText = convertHtmlToText(
        stripChipTagsFromHtml(enrichPromptData?.instructions),
      );
      if (show) {
        const result = generateEmailPreviewMergeTag({
          content: convertHTMLToText || '',
          mergeTags: fields || [],
          attributes: selectors?.attributes,
        });
        return { content: result.content, error: result.error };
      }
    }, [
      enrichPromptData?.instructions,
      fields,
      selectors?.attributes,
      show,
      generateEmailPreviewMergeTag,
      selectedProspect,
    ]);

    // Get the preview content to display
    const getPreviewContent = useMemo(() => {
      const storedPreview = getStoredPreview(selectedPerson);
      if (storedPreview) {
        return storedPreview.preview;
      }
      return aiColumnTemplatePreviewResponse?.preview;
    }, [aiColumnTemplatePreviewResponse, storedPreviews, selectedPerson]);

    const handlePrev = () => {
      // Only navigate between stored previews if limit is reached
      if (shouldDisableNewSelection) {
        const storedKeys = storedPreviews.map((p) => p.prospectKey);
        const currentIndex = storedKeys.indexOf(selectedPerson);
        const prevIndex =
          currentIndex > 0 ? currentIndex - 1 : storedKeys.length - 1;
        setSelectedPerson(storedKeys[prevIndex]);
      } else {
        const currentIndex = ProspectOptionList.findIndex(
          (p) => p.key === selectedPerson,
        );
        const prevIndex =
          (currentIndex - 1 + ProspectOptionList.length) %
          ProspectOptionList.length;
        setSelectedPerson(ProspectOptionList[prevIndex].key);
      }
    };

    const handleNext = () => {
      // Only navigate between stored previews if limit is reached
      if (shouldDisableNewSelection) {
        const storedKeys = storedPreviews.map((p) => p.prospectKey);
        const currentIndex = storedKeys.indexOf(selectedPerson);
        const nextIndex = (currentIndex + 1) % storedKeys.length;
        setSelectedPerson(storedKeys[nextIndex]);
      } else {
        const currentIndex = ProspectOptionList.findIndex(
          (p) => p.key === selectedPerson,
        );
        const nextIndex = (currentIndex + 1) % ProspectOptionList.length;
        setSelectedPerson(ProspectOptionList[nextIndex].key);
      }
    };

    // Effect's
    // eslint-disable-next-line consistent-return
    useEffect(() => {
      if (!show) {
        setStoredPreviews([]);
        setIsLoadingState(false);
        return;
      }

      if (!hasStoredPreview(selectedPerson)) {
        const timeoutId = setTimeout(() => {
          setIsLoadingState(true);
          getSingleProspectDetailsRequests(selectedProspect?.id);
        }, 50);

        // eslint-disable-next-line consistent-return
        return () => clearTimeout(timeoutId);
      }

      // If we already have a stored preview, make sure loading is off
      setIsLoadingState(false);
    }, [show, selectedProspect?.id]);

    useEffect(() => {
      executeOnRequestStatus({
        status: getSingleProspectDetailsRequestStatus,
        onSuccess: () => {
          // Only generate preview if we don't have a stored preview and haven't reached the limit
          if (!hasStoredPreview(selectedPerson) && storedPreviews.length < 5) {
            const payload = {
              aiModelId: Number(enrichPromptData?.aiModelId),
              instructions:
                br2nl(emailPreviewPrompt?.content)
                  ?.replace(/<[^>]*>/g, '')
                  ?.trim() || '',
              prospectId: selectedProspect?.id,
              selectedOutputFormat: enrichPromptData?.selectedOutputFormat,
              isWebSearchEnabled: enrichPromptData?.isWebSearchEnabled,
              ...(enrichPromptData?.customOutputFormatInput
                ? {
                    customOutputFormatPrompt:
                      enrichPromptData?.customOutputFormatInput,
                  }
                : {}),
            };
            if (show && !emailPreviewPrompt?.error) {
              // Keep loading state active while making AI preview request
              getAiColumnPreviewRequests(payload);
            } else {
              setIsLoadingState(false);
            }
          } else {
            setIsLoadingState(false);
          }
          resetSingleProspectDetailsRequests();
        },
        onFailed: () => {
          setIsLoadingState(false);
        },
      });
    }, [
      getSingleProspectDetailsRequestStatus,
      show,
      enrichPromptData,
      selectedProspect,
      selectedPerson,
      storedPreviews,
      emailPreviewPrompt?.error,
    ]);

    // Store preview response when it's successful
    useEffect(() => {
      executeOnRequestStatus({
        status: aiColumnTemplatePreviewRequestStatus,
        onSuccess: () => {
          if (
            aiColumnTemplatePreviewResponse?.preview &&
            !hasStoredPreview(selectedPerson)
          ) {
            setStoredPreviews((prev) => [
              ...prev,
              {
                prospectKey: selectedPerson,
                preview: aiColumnTemplatePreviewResponse.preview,
                prospectId: selectedProspect?.id,
              },
            ]);
          }
          setIsLoadingState(false);
          resetSingleProspectDetailsRequests();
          resetAiColumnPreview();
        },
        onFailed: () => {
          setIsLoadingState(false);
        },
      });
    }, [aiColumnTemplatePreviewRequestStatus]);

    useEffect(() => {
      setIsCreditsPopoverOpen(false);
    }, [
      selectedPerson,
      getSingleProspectDetailsRequestStatus,
      aiColumnTemplatePreviewRequestStatus,
    ]);

    return (
      <Modal
        title={undefined}
        show={show}
        onHide={handleClose}
        backdrop={false}
        hidePrimaryBtn
        hideSecondaryBtn
        className="preview-modal"
        footer={
          <div className="preview-footer">
            <div className="info-circle-container">
              <InfoCircle className="info-circle" />
            </div>
            {isLoadingState ? (
              <div className="preview-text">
                <span>This type of generation typically consumes </span>
                <OverlayPopover
                  className="credits-popover"
                  content={
                    <div className="credits-popover-content">
                      <div className="credits-popover-text">
                        <div className="title">
                          Credits shown are estimated. It may vary based upon
                          token consumption.
                        </div>
                        <a
                          href="https://docs.saleshandy.com/en/articles/12412342-a-complete-guide-to-saleshandy-ai-credits"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="learn-more"
                        >
                          Learn More
                        </a>
                      </div>
                      <div
                        className="cross-icon-container"
                        {...accessibleOnClick(() => {
                          setIsCreditsPopoverOpen(false);
                        })}
                      >
                        <Cross width={15} height={15} className="cross-icon" />
                      </div>
                    </div>
                  }
                  placement={Placement.BottomEnd}
                  show={isCreditsPopoverOpen}
                  onToggle={setIsCreditsPopoverOpen}
                  rootClose={true}
                >
                  <span className="credits">
                    {selectedModalAiCreditUsagePerRow} AI Credits.
                  </span>
                </OverlayPopover>
              </div>
            ) : shouldDisableNewSelection ? (
              <span className="preview-text">
                Previews are limited for up to 5 prospects per prompt.
              </span>
            ) : (
              <span className="preview-text">
                Preview output for 5 prospects to see results before applying to
                all.
              </span>
            )}
          </div>
        }
      >
        <div className="preview-modal-container">
          <div className="preview-modal-header">
            <span className="modal-title">Model Response Preview</span>
            <div
              className="cross-icon-container"
              {...accessibleOnClick(() => {
                handleClose();
              })}
            >
              <Cross width={16} height={16} className="cross-icon" />
            </div>
          </div>
          <div className="preview-modal-body">
            <div className="person-select-container">
              <Select
                options={ProspectOptionList.map((option) => ({
                  ...option,
                  disabled: isProspectDisabled(option.key),
                }))}
                disabled={disbaleBtn}
                className="person-select"
                optionRenderer={(option) => (
                  <div
                    className={`person-select-dropdown ${
                      option.disabled ? 'disabled' : ''
                    }`}
                  >
                    <span>{option?.label?.trim() || '(No Name)'}</span>
                    <br />
                    <span className="email">{option?.email}</span>
                  </div>
                )}
                selectedOptionRenderer={([option]) => (
                  <span>{option?.label?.trim() || '(No Name)'}</span>
                )}
                selectedOptionKey={selectedPerson}
                onChange={([option]) => {
                  if (!option.disabled) {
                    setSelectedPerson(option.key);
                    setIsLoadingState(false);
                    resetAiColumnTemplatePreviewRequest();
                  }
                }}
                showSearch
                filterOption={(value, option) =>
                  option?.label?.toLowerCase().includes(value.toLowerCase())
                }
              />
              <div
                className={`chewron-container ${disbaleBtn ? 'disabled' : ''}`}
              >
                <OverlayTooltip text="Previous Prospect">
                  <button
                    type="button"
                    className="chewron-icon"
                    {...accessibleOnClick(handlePrev)}
                    disabled={disbaleBtn}
                  >
                    <ChevronLeft />
                  </button>
                </OverlayTooltip>
                <div className="chewron-divider" />
                <OverlayTooltip text="Next Prospect">
                  <button
                    type="button"
                    className="chewron-icon"
                    {...accessibleOnClick(handleNext)}
                    disabled={disbaleBtn}
                  >
                    <ChevronRight />
                  </button>
                </OverlayTooltip>
              </div>
            </div>

            <div className="preview-modal-content">
              {isLoadingState ? (
                <div className="animation-content">
                  <div>
                    <Lottie
                      animationData={AiVariant3StarIcon}
                      loop
                      className="preview-modal-animation"
                    />
                    <span>Generating preview...</span>
                  </div>
                </div>
              ) : (
                <div className="generated-preview">
                  {aiColumnTemplatePreviewRequestStatus ===
                    RequestStatus.Ideal && !emailPreviewPrompt?.error ? (
                    <div className="generated-preview-content">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: getPreviewContent,
                        }}
                      />
                    </div>
                  ) : (
                    <span className="preview-modal-no-merge-tag">
                      {(aiColumnTemplatePreviewRequestStatus as RequestStatus) ===
                      RequestStatus.Failed
                        ? 'Please try again later'
                        : 'Merge Tag Value not Found'}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal>
    );
  },
);

export default AiColPromptPreviewModal;
